import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, FilterList } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY      = '#1a237e';
const PRIMARY_MID  = '#283593';
const PRIMARY_DARK = '#0d1b6e';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt2Date = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
};
const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  if (isNaN(d)) return '-';
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};
const fmtN = (n, dec = 3) =>
  (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const qtyColor = (n) => {
  const v = parseFloat(n) || 0;
  if (v > 0)  return '#1b5e20';
  if (v < 0)  return '#b71c1c';
  return '#546e7a';
};

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.15)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

// ─── Column definitions ────────────────────────────────────────────────────────
const COLUMNS = [
  { key: '#',              label: '#',              align: 'center', width: 36  },
  { key: 'Code',           label: 'Item Code',      align: 'left',   width: 100 },
  { key: 'Name',           label: 'Item Name',      align: 'left',   width: 220 },
  { key: 'OpeningQty',     label: 'Opening Qty',    align: 'right',  width: 110 },
  { key: 'TransactionQty', label: 'Transaction Qty',align: 'right',  width: 120 },
  { key: 'ClosingQty',     label: 'Closing Qty',    align: 'right',  width: 110 },
  { key: 'CHR',            label: 'CHR',            align: 'right',  width: 90  },
  { key: 'NPR',            label: 'NPR',            align: 'right',  width: 90  },
  { key: 'GD01',           label: 'GD01',           align: 'right',  width: 90  },
  { key: 'GD02',           label: 'GD02',           align: 'right',  width: 90  },
  { key: 'GD03',           label: 'GD03',           align: 'right',  width: 90  },
  { key: 'GD04',           label: 'GD04',           align: 'right',  width: 90  },
  { key: 'GD05',           label: 'GD05',           align: 'right',  width: 90  },
];

const QTY_KEYS = ['OpeningQty','TransactionQty','ClosingQty','CHR','NPR','GD01','GD02','GD03','GD04','GD05'];

// ─── Main Component ────────────────────────────────────────────────────────────
const StockBranchWiseReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate,    setFromDate]    = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate,      setToDate]      = useState(fmt2Date(today));
  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [fetched,     setFetched]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── Dropdown data ──
  const [branches,   setBranches]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands,     setBrands]     = useState([]);
  const [allItems,   setAllItems]   = useState([]);

  // ── Selected filters ──
  const [selBranch,   setSelBranch]   = useState('');
  const [selCategory, setSelCategory] = useState('');
  const [selBrand,    setSelBrand]    = useState('');
  const [selItem,     setSelItem]     = useState('');

  // ── Load master dropdowns ──────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !token) return;
    Promise.allSettled([
      fetchData('BranchApi',   `/${companyId}`, token),
      fetchData('CategoryApi', `/${companyId}`, token),
      fetchData('BrandApi',    `/${companyId}`, token),
      fetchData('ItemApi',     `/${companyId}`, token),
    ]).then(([brRes, catRes, brndRes, itemRes]) => {
      if (brRes.status   === 'fulfilled') setBranches  (brRes.value?.response_data   || brRes.value   || []);
      if (catRes.status  === 'fulfilled') setCategories(catRes.value?.response_data  || catRes.value  || []);
      if (brndRes.status === 'fulfilled') setBrands    (brndRes.value?.response_data || brndRes.value || []);
      if (itemRes.status === 'fulfilled') setAllItems  (itemRes.value?.response_data || itemRes.value || []);
    });
  }, [companyId, token]);

  // ── Default branch ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (branchId && !selBranch) setSelBranch(String(branchId));
  }, [branchId]); // eslint-disable-line

  // ── Cascaded item list ──────────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    let items = allItems;
    if (selCategory) items = items.filter(i => String(i.CategoryId) === String(selCategory));
    if (selBrand)    items = items.filter(i => String(i.BrandId)    === String(selBrand));
    return items;
  }, [allItems, selCategory, selBrand]);

  useEffect(() => {
    if (selItem && !filteredItems.find(i => String(i.Id) === String(selItem))) setSelItem('');
  }, [filteredItems]); // eslint-disable-line

  // ── Auto-fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // ── Fetch report ────────────────────────────────────────────────────────────
  const fetchReport = async () => {
    if (!fromDate || !toDate)                      { setError('Please select date range'); return; }
    if (new Date(fromDate) > new Date(toDate))     { setError('From Date cannot be greater than To Date'); return; }
    if (!companyId || !token)                      { setError('Authentication error'); return; }

    const branchParam   = selBranch   ? parseInt(selBranch)   : (branchId ? parseInt(branchId) : 0);
    const categoryParam = selCategory ? parseInt(selCategory) : 0;
    const brandParam    = selBrand    ? parseInt(selBrand)    : 0;
    const itemParam     = selItem     ? parseInt(selItem)     : 0;

    setLoading(true); setError('');
    try {
      const res = await fetchData(
        'ReportApi',
        `/StockBranchWise/${fromDate}/${toDate}/${companyId}/${branchParam}/${categoryParam}/${brandParam}/${itemParam}/${itemParam}`,
        token
      );
      const rows = res?.response_data ?? (Array.isArray(res) ? res : []);
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error('StockBranchWiseReport error:', err);
      setError(`Failed to fetch report: ${err?.response?.status ? `HTTP ${err.response.status}` : err?.message || 'Unknown error'}`);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Client-side search ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.Code         || '').toLowerCase().includes(q) ||
      (r.Name         || '').toLowerCase().includes(q) ||
      (r.CategoryName || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // ── Totals ───────────────────────────────────────────────────────────────────
  const totals = useMemo(() => filtered.reduce((acc, r) => {
    QTY_KEYS.forEach(k => { acc[k] = (acc[k] || 0) + (parseFloat(r[k]) || 0); });
    return acc;
  }, {}), [filtered]);

  // ── Group by Category ────────────────────────────────────────────────────────
  const groupedByCategory = useMemo(() => {
    const map = new Map();
    filtered.forEach(r => {
      const key = r.CategoryName || 'Uncategorized';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const categorySubtotal = (rows) => rows.reduce((acc, r) => {
    QTY_KEYS.forEach(k => { acc[k] = (acc[k] || 0) + (parseFloat(r[k]) || 0); });
    return acc;
  }, {});

  // ── Excel export ─────────────────────────────────────────────────────────────
  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'StockBranchWiseReport', 0);
    if (!filtered.length) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Stock Branch Wise');
    const totalCols = COLUMNS.length;
    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 5 }];
    const thin = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };

    const addMerged = (text, font, fill, height) => {
      const rn = ws.rowCount + 1;
      ws.mergeCells(rn, 1, rn, totalCols);
      const cell = ws.getCell(`A${rn}`);
      cell.value = text; cell.font = font;
      cell.alignment = { horizontal:'center', vertical:'middle' };
      if (fill) cell.fill = fill;
      ws.getRow(rn).height = height || 18;
    };

    addMerged(companyName || 'Company',
      { bold:true, size:14, color:{argb:'FF1a237e'} },
      { type:'pattern', pattern:'solid', fgColor:{argb:'FFe8eaf6'} }, 22);
    addMerged('Stock Branch Wise Report',
      { bold:true, size:12, color:{argb:'FF283593'} }, null, 18);
    addMerged(`Period: ${fmtDate(fromDate)} to ${fmtDate(toDate)}`,
      { size:10, color:{argb:'FF37474f'} }, null, 16);
    addMerged(`Generated on: ${new Date().toLocaleString()} | By: ${username}`,
      { italic:true, size:9, color:{argb:'FF9e9e9e'} }, null, 14);

    const hdr = ws.addRow(COLUMNS.map(c => c.label));
    hdr.height = 18;
    hdr.eachCell((cell, ci) => {
      cell.font      = { bold:true, size:9, color:{argb:'FFFFFFFF'} };
      cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:'FF0d1b6e'} };
      cell.alignment = { horizontal: COLUMNS[ci-1]?.align === 'right' ? 'right' : 'center', vertical:'middle' };
      cell.border    = thin;
    });

    let globalIdx = 0;
    groupedByCategory.forEach(([catName, catRows]) => {
      // Category header
      const cHdr = ws.addRow(COLUMNS.map((_, ci) => ci === 0 ? '' : ci === 1 ? `  ${catName}` : ''));
      cHdr.height = 15;
      cHdr.eachCell(cell => {
        cell.font  = { bold:true, size:10, color:{argb:'FFFFFFFF'} };
        cell.fill  = { type:'pattern', pattern:'solid', fgColor:{argb:'FF283593'} };
        cell.border = thin;
        cell.alignment = { horizontal:'left', vertical:'middle' };
      });
      if (totalCols > 2) ws.mergeCells(cHdr.number, 2, cHdr.number, totalCols);

      catRows.forEach((r, i) => {
        globalIdx++;
        const row = ws.addRow(COLUMNS.map(c => {
          if (c.key === '#') return globalIdx;
          if (QTY_KEYS.includes(c.key)) return parseFloat(r[c.key]) || 0;
          return r[c.key] ?? '';
        }));
        const bg = i % 2 === 0 ? 'FFFFFFFF' : 'FFf3f4fd';
        row.eachCell((cell, ci) => {
          cell.border    = thin;
          cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:bg} };
          cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
          cell.font      = { size:9 };
        });
      });

      // Category subtotal
      const sub = categorySubtotal(catRows);
      const stRow = ws.addRow(COLUMNS.map(c => {
        if (c.key === '#')   return '';
        if (c.key === 'Code') return `Subtotal — ${catName}`;
        if (c.key === 'Name') return `${catRows.length} item${catRows.length !== 1 ? 's' : ''}`;
        if (QTY_KEYS.includes(c.key)) return parseFloat(sub[c.key]) || 0;
        return '';
      }));
      stRow.eachCell((cell, ci) => {
        cell.font   = { bold:true, size:9, color:{argb:'FF1a237e'} };
        cell.fill   = { type:'pattern', pattern:'solid', fgColor:{argb:'FFe8eaf6'} };
        cell.border = thin;
        cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
      });
    });

    // Grand total
    const gtRow = ws.addRow(COLUMNS.map(c => {
      if (c.key === '#')   return '';
      if (c.key === 'Code') return 'GRAND TOTAL';
      if (c.key === 'Name') return `${filtered.length} Items · ${groupedByCategory.length} Categories`;
      if (QTY_KEYS.includes(c.key)) return parseFloat(totals[c.key]) || 0;
      return '';
    }));    gtRow.eachCell((cell, ci) => {
      cell.font   = { bold:true, size:10, color:{argb:'FFFFFFFF'} };
      cell.fill   = { type:'pattern', pattern:'solid', fgColor:{argb:'FF0d47a1'} };
      cell.border = thin;
      cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
    });

    ws.columns = COLUMNS.map(c => ({ width: Math.max((c.width || 90) / 7, 8) }));
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `StockBranchWise_${fromDate}_to_${toDate}.xlsx`);
  };

  // ── Cell renderer ─────────────────────────────────────────────────────────────
  const renderCell = (col, row) => {
    const v = row[col.key];
    if (QTY_KEYS.includes(col.key)) {
      return <span style={{ color: qtyColor(v), fontWeight: 600 }}>{fmtN(v)}</span>;
    }
    return v ?? '-';
  };

  // ── Styles ────────────────────────────────────────────────────────────────────
  const thStyle = (col) => ({
    position:'sticky', top:0, zIndex:3,
    background: PRIMARY_DARK, color:'#fff',
    fontWeight:700, fontSize:10, textTransform:'uppercase', letterSpacing:'0.4px',
    padding:'7px 8px', borderRight:'1px solid rgba(255,255,255,0.12)',
    borderBottom:'2px solid rgba(255,255,255,0.2)',
    whiteSpace:'nowrap', textAlign: col.align,
    minWidth: col.width, width: col.width,
  });
  const tdStyle = (col, i) => ({
    padding:'5px 8px', fontSize:11, textAlign: col.align,
    borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #f1f5f9',
    whiteSpace:'nowrap', color:'#374151',
    background: i % 2 === 0 ? '#ffffff' : '#f3f4fd',
  });

  // ── KPI cards ─────────────────────────────────────────────────────────────────
  const kpis = [
    { label:'Items',       value: filtered.length,             color: PRIMARY_MID, bg:'#e8eaf6' },
    { label:'Opening Qty', value: fmtN(totals.OpeningQty),     color:'#0277bd',    bg:'#e1f5fe' },
    { label:'Transaction', value: fmtN(totals.TransactionQty), color:'#6a1b9a',    bg:'#f3e5f5' },
    { label:'Closing Qty', value: fmtN(totals.ClosingQty),     color:'#00695c',    bg:'#e0f2f1' },
    { label:'CHR',         value: fmtN(totals.CHR),            color:'#d84315',    bg:'#fbe9e7' },
    { label:'NPR',         value: fmtN(totals.NPR),            color:'#558b2f',    bg:'#f1f8e9' },
    { label:'GD01',        value: fmtN(totals.GD01),           color:'#616161',    bg:'#f5f5f5' },
    { label:'GD02',        value: fmtN(totals.GD02),           color:'#5d4037',    bg:'#efebe9' },
    { label:'GD03',        value: fmtN(totals.GD03),           color:'#37474f',    bg:'#eceff1' },
    { label:'GD04',        value: fmtN(totals.GD04),           color:'#4e342e',    bg:'#efebe9' },
    { label:'GD05',        value: fmtN(totals.GD05),           color:'#263238',    bg:'#eceff1' },
  ];

  // ── Dropdown options ──────────────────────────────────────────────────────────
  const branchOpts   = [{ value:'', label:'— All Branches —' },   ...branches.map(b => ({ value:String(b.Id), label: b.Name || b.BranchName || '' }))];
  const categoryOpts = [{ value:'', label:'— All Categories —' }, ...categories.map(c => ({ value:String(c.Id), label: c.Name || '' }))];
  const brandOpts    = [{ value:'', label:'— All Brands —' },     ...brands.map(b => ({ value:String(b.Id), label: b.Name || '' }))];
  const itemOpts     = [{ value:'', label:'— All Items —' },      ...filteredItems.map(i => ({ value:String(i.Id), label: i.Name ? `${i.Code ? i.Code+' – ' : ''}${i.Name}` : String(i.Id) }))];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100%', background:'#f0f2f5', padding:10, fontFamily:"'Inter','Segoe UI',sans-serif", boxSizing:'border-box' }}>
      <LogoLoader open={loading} message="Loading Stock Branch Wise Report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width:'100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.09)', border:'1px solid #e2e8f0', overflow:'hidden' }}>

        {/* ── Toolbar ── */}
        <div style={{
          display:'flex', alignItems:'center', padding:'6px 12px',
          background:`linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 60%, ${PRIMARY_MID} 100%)`,
          gap:6, flexWrap:'nowrap', overflowX:'auto', overflowY:'hidden', scrollbarWidth:'none',
          boxShadow:'0 2px 8px rgba(13,71,161,0.35)',
        }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          <div style={{ flexShrink:0 }}>
            <div style={{ fontSize:13, fontWeight:800, color:'#fff', whiteSpace:'nowrap', letterSpacing:'0.3px' }}>Stock Branch Wise Report</div>
            <div style={{ fontSize:9, color:'rgba(255,255,255,0.65)', whiteSpace:'nowrap' }}>Item stock summary by branch</div>
          </div>

          <div style={{ flex:1, minWidth:4 }} />

          {/* Search */}
          <input
            placeholder="Search item / code / category..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 10px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark',
              flexShrink:0, minWidth:200, outline:'none' }}
          />

          {/* From Date */}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 8px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark', flexShrink:0 }} />

          {/* To Date */}
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 8px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark', flexShrink:0 }} />

          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          {/* Filter toggle */}
          <Tooltip title={filtersOpen ? 'Hide Filters' : 'Show Filters'} arrow>
            <IconButton
              onClick={() => setFiltersOpen(o => !o)}
              size="small"
              sx={{
                ...iconBtn,
                background: filtersOpen ? 'rgba(255,255,255,0.22)' : 'transparent',
              }}
            >
              <FilterList sx={{ fontSize:16 }} />
              {[selCategory, selBrand, selItem].filter(Boolean).length > 0 && (
                <span style={{
                  position:'absolute', top:2, right:2, width:7, height:7, borderRadius:'50%',
                  background:'#ffeb3b', border:'1px solid rgba(0,0,0,0.2)',
                }} />
              )}
            </IconButton>
          </Tooltip>
        </div>

        {/* ── Filter Panel ── */}
        {filtersOpen && (
          <div style={{
            background:`linear-gradient(180deg, #1a237e 0%, #283593 100%)`,
            borderBottom:'2px solid rgba(255,255,255,0.15)',
            padding:'10px 16px 12px',
          }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <span style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.85)', letterSpacing:'0.5px', textTransform:'uppercase' }}>
                🔍 Filter Options
              </span>
              {[selCategory, selBrand, selItem, String(selBranch) !== String(branchId) ? '1' : ''].filter(Boolean).length > 0 && (
                <button
                  onClick={() => { setSelBranch(String(branchId)); setSelCategory(''); setSelBrand(''); setSelItem(''); }}
                  style={{ height:24, border:'1px solid rgba(255,255,255,0.4)', borderRadius:5,
                    background:'rgba(255,80,80,0.25)', color:'#ffcdd2', fontSize:10, padding:'0 10px', cursor:'pointer', fontWeight:700 }}
                >
                  ✕ Clear All
                </button>
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'8px 10px' }}>
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>Branch</div>
                <SearchableDropdown value={selBranch} onChange={v => setSelBranch(v)}
                  options={branchOpts} placeholder="— All Branches —" minWidth={0} primaryColor={PRIMARY} />
              </div>
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>Category</div>
                <SearchableDropdown value={selCategory} onChange={v => { setSelCategory(v); setSelItem(''); }}
                  options={categoryOpts} placeholder="— All Categories —" minWidth={0} primaryColor={PRIMARY} />
              </div>
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>Brand</div>
                <SearchableDropdown value={selBrand} onChange={v => { setSelBrand(v); setSelItem(''); }}
                  options={brandOpts} placeholder="— All Brands —" minWidth={0} primaryColor={PRIMARY} />
              </div>
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>
                  Item {(selCategory || selBrand) ? `(${filteredItems.length})` : ''}
                </div>
                <SearchableDropdown value={selItem} onChange={v => setSelItem(v)}
                  options={itemOpts}
                  placeholder={selCategory || selBrand ? `— ${filteredItems.length} items —` : '— All Items —'}
                  minWidth={0} primaryColor={PRIMARY} />
              </div>
            </div>
          </div>
        )}

        {/* ── KPI Strip ── */}
        {fetched && filtered.length > 0 && (
          <div style={{ display:'flex', flexWrap:'nowrap', gap:0, borderBottom:'1px solid #e8eaf6', overflowX:'auto', scrollbarWidth:'none' }}>
            {kpis.map((k, i) => (
              <div key={k.label} style={{
                flex:1, minWidth:90, padding:'7px 10px', textAlign:'center',
                background: k.bg, borderRight: i < kpis.length-1 ? '1px solid #e8eaf6' : 'none',
              }}>
                <div style={{ fontSize:9, color:'#78909c', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px' }}>{k.label}</div>
                <div style={{ fontSize:13, fontWeight:800, color:k.color, lineHeight:1.3, whiteSpace:'nowrap' }}>{k.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Meta row ── */}
        {fetched && (
          <div style={{ padding:'4px 14px', fontSize:11, color:'#64748b', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:12 }}>
            <span>{fmtDate(fromDate)} – {fmtDate(toDate)}</span>
            <span>·</span>
            <span><strong>{filtered.length}</strong> items{search && ` (filtered from ${data.length})`}</span>
            <span>·</span>
            <span><strong>{groupedByCategory.length}</strong> categor{groupedByCategory.length !== 1 ? 'ies' : 'y'}</span>
            {selBranch && <><span>·</span><span>Branch: <strong>{branches.find(b => String(b.Id) === selBranch)?.Name || selBranch}</strong></span></>}
          </div>
        )}

        {/* ── Data Table ── */}
        <div style={{
          overflowX:'auto', overflowY:'auto',
          maxHeight:'calc(100vh - 255px)',
          position:'relative', scrollbarWidth:'thin',
          scrollbarColor:`${PRIMARY_MID} #e8eaf6`,
        }}>
          <table style={{ borderCollapse:'collapse', fontSize:11, width:'100%', minWidth:1400 }}>
            <thead>
              <tr>{COLUMNS.map(col => <th key={col.key} style={thStyle(col)}>{col.label}</th>)}</tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ textAlign:'center', padding:48, color:'#94a3b8', fontSize:14 }}>
                    {!fetched ? 'Select date range and click Refresh to load data' : 'No records found for the selected criteria'}
                  </td>
                </tr>
              ) : (() => {
                let globalIdx = 0;
                return groupedByCategory.map(([catName, catRows]) => {
                  const sub = categorySubtotal(catRows);
                  return (
                    <React.Fragment key={catName}>

                      {/* ── Category group header ── */}
                      <tr>
                        <td colSpan={COLUMNS.length} style={{
                          background:'linear-gradient(90deg, #0d1b6e 0%, #1a237e 60%, #283593 100%)',
                          color:'#fff', fontWeight:700, fontSize:11,
                          padding:'5px 12px', letterSpacing:'0.3px',
                          borderTop:'2px solid #3949ab', borderBottom:'1px solid rgba(255,255,255,0.15)',
                        }}>
                          📦 {catName}
                          <span style={{ marginLeft:10, fontSize:9, fontWeight:400, background:'rgba(255,255,255,0.18)', borderRadius:4, padding:'1px 7px', verticalAlign:'middle' }}>
                            {catRows.length} item{catRows.length !== 1 ? 's' : ''}
                          </span>
                        </td>
                      </tr>

                      {/* ── Data rows ── */}
                      {catRows.map((row, i) => {
                        const rowIdx = globalIdx++;
                        return (
                          <tr key={`${row.Id}-${rowIdx}`}
                            onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#ffffff' : '#f3f4fd'; }}
                          >
                            {COLUMNS.map(col => (
                              <td key={col.key} style={tdStyle(col, i)}>
                                {col.key === '#' ? rowIdx + 1 : renderCell(col, row)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}

                      {/* ── Category subtotal ── */}
                      <tr>
                        {COLUMNS.map(col => {
                          const stStyle = {
                            background:'#e8eaf6', color:'#1a237e', fontWeight:700, fontSize:10,
                            padding:'5px 8px', whiteSpace:'nowrap',
                            borderTop:'1px solid #c5cae9', borderBottom:'2px solid #9fa8da',
                            borderRight:'1px solid #c5cae9', textAlign: col.align,
                          };
                          if (col.key === '#')            return <td key={col.key} style={stStyle}></td>;
                          if (col.key === 'Code')          return <td key={col.key} style={{...stStyle, textAlign:'right', color:'#37474f'}}>Subtotal</td>;
                          if (col.key === 'Name')          return <td key={col.key} style={{...stStyle, color:'#37474f', fontStyle:'italic'}}>{catName} — {catRows.length} item{catRows.length !== 1 ? 's' : ''}</td>;
                          if (QTY_KEYS.includes(col.key))  return <td key={col.key} style={{...stStyle, color: qtyColor(sub[col.key])}}>{fmtN(sub[col.key] || 0)}</td>;
                          return <td key={col.key} style={stStyle}></td>;
                        })}
                      </tr>

                    </React.Fragment>
                  );
                });
              })()}
            </tbody>

            {/* ── Sticky grand-total footer ── */}
            {filtered.length > 0 && (
              <tfoot>
                <tr>
                  {COLUMNS.map(col => {
                    const tfStyle = {
                      position:'sticky', bottom:0, zIndex:2,
                      background:`linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MID} 100%)`,
                      color:'#fff', fontWeight:700, fontSize:10,
                      padding:'6px 8px', borderTop:'2px solid rgba(255,255,255,0.2)',
                      borderRight:'1px solid rgba(255,255,255,0.1)',
                      textAlign: col.align, whiteSpace:'nowrap',
                    };
                    if (col.key === '#')            return <td key={col.key} style={tfStyle}></td>;
                    if (col.key === 'Code')          return <td key={col.key} style={{...tfStyle, textAlign:'right', fontSize:11}}>TOTALS</td>;
                    if (col.key === 'Name')          return <td key={col.key} style={{...tfStyle, color:'rgba(255,255,255,0.7)', fontStyle:'italic'}}>{filtered.length} Items · {groupedByCategory.length} Categor{groupedByCategory.length !== 1 ? 'ies' : 'y'}</td>;
                    if (col.key === 'OpeningQty')    return <td key={col.key} style={{...tfStyle, color:'#b3e5fc'}}>{fmtN(totals.OpeningQty || 0)}</td>;
                    if (col.key === 'TransactionQty')return <td key={col.key} style={{...tfStyle, color:'#ce93d8'}}>{fmtN(totals.TransactionQty || 0)}</td>;
                    if (col.key === 'ClosingQty')    return <td key={col.key} style={{...tfStyle, color:'#a5d6a7'}}>{fmtN(totals.ClosingQty || 0)}</td>;
                    if (col.key === 'CHR')           return <td key={col.key} style={{...tfStyle, color:'#ffab91'}}>{fmtN(totals.CHR || 0)}</td>;
                    if (col.key === 'NPR')           return <td key={col.key} style={{...tfStyle, color:'#c8e6c9'}}>{fmtN(totals.NPR || 0)}</td>;
                    if (col.key === 'GD01')          return <td key={col.key} style={{...tfStyle, color:'#e0e0e0'}}>{fmtN(totals.GD01 || 0)}</td>;
                    if (col.key === 'GD02')          return <td key={col.key} style={{...tfStyle, color:'#e0e0e0'}}>{fmtN(totals.GD02 || 0)}</td>;
                    if (col.key === 'GD03')          return <td key={col.key} style={{...tfStyle, color:'#e0e0e0'}}>{fmtN(totals.GD03 || 0)}</td>;
                    if (col.key === 'GD04')          return <td key={col.key} style={{...tfStyle, color:'#e0e0e0'}}>{fmtN(totals.GD04 || 0)}</td>;
                    if (col.key === 'GD05')          return <td key={col.key} style={{...tfStyle, color:'#e0e0e0'}}>{fmtN(totals.GD05 || 0)}</td>;
                    return <td key={col.key} style={tfStyle}></td>;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

      </div>
    </div>
  );
};

export default StockBranchWiseReport;
